import { colors } from "@itandi/itandi-bb-ui";
import type { Th } from "@itandi/itandi-bb-ui/Th";
import type { ComponentProps } from "react";
import type { CompanySetting } from "~/models/CompanySetting";
import type { DealBillingContext } from "~/models/DealBilling";
import type { DealCancel } from "~/models/DealCancel";
import type { DealConstructionSchedule } from "~/models/DealConstructionSchedule";
import type { DealEstimationContext } from "~/models/DealEstimate";
import type { DealHistory } from "~/models/DealHistory";
import type { DealUploadFile } from "~/models/DealUploadFile";
import type { DealVendorCompany } from "~/models/DealVendorCompany";
import { getOriginDealVendor } from "~/models/DealVendorCompany";
import type { InspectionCandidateDate } from "~/models/InspectionCandidateDate";
import type { MessageSummaryUnread } from "~/models/MessageSummaryUnread";
import type { Staff } from "~/models/Staff";
import type { StatusId } from "~/models/Status";
import { isOwnStatus } from "~/models/Status";
import type { Subtype } from "~/models/Subtype";
import type { PartnerDeal } from "~/models/sk/PartnerDeal";
import type { VendorCompany } from "~/models/VendorCompany";
import type { VendorUser } from "~/models/VendorUser";
import { truncate } from "~/utils/StringUtil";
import type { CompanyStoreSetting } from "./CompanyStoreSetting";
import type { DealEstimationHistoryContext } from "./DealEstimationHistory";
import type { DealRepair } from "./DealRepair";
import type { DealStatusReason } from "./DealStatusReason";
import type { PropertyBuildingOption } from "./PropertyBuildingOption";
import type { PropertyRoomOption } from "./PropertyRoomOption";

export type DealType = "restoration" | "repair" | "other";
export const DEAL_TYPES = [
	"restoration",
	"repair",
	"other"
] as const satisfies readonly DealType[];
export type InspectionBy = "vendor" | "company" | "none";
export const ESTIMATION_BY = { COMPANY: "company", VENDOR: "vendor" } as const;
type EstimationBy = (typeof ESTIMATION_BY)[keyof typeof ESTIMATION_BY];

export type Deal = {
	address: string;
	buildingPropertyId: number | null;
	companySetting: CompanySetting;
	companyStoreSetting?: { name: string };
	completedDate: string | null;
	completionReportImageRequired?: boolean;
	conductFieldSurvey: boolean;
	createdAt: string;
	dealCancel: DealCancel | null;
	dealCompanyPrivateFiles?: readonly DealUploadFile[];
	dealConstructionSchedules: DealConstructionSchedule[];
	dealInspectionCandidateDates: InspectionCandidateDate[];
	dealMemorandumFiles: DealUploadFile[];
	dealOtherFiles: DealUploadFile[];
	dealReceiptFiles: DealUploadFile[];
	dealRepair: DealRepair | null;
	dealStatusReasons: DealStatusReason[];
	dealSubtype: Subtype | null;
	dealType: DealType;
	dealVendorCompanies: DealVendorCompany[];
	dealVendorNoteId: number | null;
	enableAutoLock: boolean;
	enableElevator: boolean;
	estimateAddressName: string;
	estimationBy: EstimationBy;
	floorMap: string;
	freeParking: string;
	hasDraftEstimation: boolean;
	hasRequestedEstimation: boolean;
	hasRequestEstimation: boolean;
	id: number;
	inspectionBy: InspectionBy;
	inspectionDate: string | null;
	inspectionReportImageRequired?: boolean;
	isRegisterAsBuilding: boolean;
	latestEstimationRejected: boolean;
	leaseContractFile1: string | null;
	leaseContractFile1OriginalFilename: string | null;
	leaseContractFile2: string | null;
	leaseContractFile2OriginalFilename: string | null;
	leaseContractFile3: string | null;
	leaseContractFile3OriginalFilename: string | null;
	leasingId: number | null;
	messageSummaryUnread: MessageSummaryUnread | null;
	name: string;
	note: string;
	placeKeyStoring: string;
	preferredDate: string;
	propertyBuildingOption?: PropertyBuildingOption;
	propertyDealId: number;
	// KT経由で作成した案件は、parking_spaceになることがある(KTの契約は駐車場区画と紐づけることができるため)
	propertyId: number | null;
	propertyRoomOption?: PropertyRoomOption | null;
	propertyType: "room" | "building" | "parking_space" | null;
	relatedDeals: RelatedDeal[];
	roomNumber: string | null;
	// 施工会社用のpartialでのみ店舗情報を返しているためオプショナル
	staff: Staff;
	statusId: StatusId;
	surveyDate: string | null;
	tenantEmail: string | null;
	tenantName: string | null;
	tenantNameKana: string | null;
	tenantTel: string | null;
	terminationDate: string;
	updatedAt: string;
};

export type DealWithCompanyAndVendor = {
	address: string;
	companySetting: CompanySetting;
	createdAt: string;
	enableAutoLock: boolean;
	enableElevator: boolean;
	estimateAddressName: string;
	floorMap: string;
	freeParking: string;
	id: number;
	inspectionDate: string;
	leaseContractFile1: string;
	leaseContractFile2: string;
	leaseContractFile3: string;
	name: string;
	note: string;
	originVendorCompany: VendorCompany | null;
	placeKeyStoring: string;
	preferredDate: string;
	roomNumber: string | null;
	staff: Staff | null;
	tenantEmail: string | null;
	tenantName: string | null;
	tenantNameKana: string | null;
	tenantTel: string | null;
	terminationDate: string;
	updatedAt: string;
};

/**
 * 案件一覧のDealModel(管理会社)
 */
export type DealIndexClient = {
	address: string;
	authId: number;
	companySetting: CompanySetting;
	companyStoreSetting: CompanyStoreSetting;
	completedDate: string;
	constructionEndDate: string | null;
	constructionReportedDate: string | null;
	constructionStartDate: string | null;
	createdAt: string;
	dealSubtype: Subtype | null;
	dealType: DealType;
	dealVendorCompanies: DealVendorCompany[];
	displayUnread: boolean;
	estimationBy: EstimationBy;
	hasDraftEstimationByCompany: boolean;
	hasRequestEstimation: boolean;
	hasRequestEstimationByCompany: boolean;
	hasTemporaryDealCancel: boolean;
	id: number;
	inspectionBy: InspectionBy;
	inspectionDate: string | null;
	leaseContractFile1: string;
	name: string;
	orderDate: string | null;
	preferredDate: string | null;
	propertyDealId: number;
	relatedDealCount: number;
	roomNumber: string | null;
	sendEstimateDate: string | null;
	staff: Staff;
	statusId: StatusId;
	surveyDate: string | null;
	tenantName: string | null;
	tenantNameKana: string | null;
	terminationDate: string | null;
	uncompletedTodoCount: number;
	unreadMessageCount: number;
	updatedAt: string;
	updatedDate: string;
	user: VendorUser;
};

/**
 * 案件一覧のDealModel(施工会社)
 */
export type DealIndexVendor = {
	address: string;
	companySetting: CompanySetting;
	completedDate: string;
	completionReportImageRequired: boolean;
	constructionEndDate: string | null;
	constructionReportedDate: string | null;
	constructionStartDate: string | null;
	createdAt: string;
	dealHistories: DealHistory[];
	dealSubtype: Subtype | null;
	dealType: DealType;
	displayUnread: boolean;
	hasDraftEstimation: boolean;
	hasRequestEstimation: boolean;
	hasRequestEstimationByCompany: boolean;
	hasTemporaryDealCancel: boolean;
	id: number;
	inspectionBy: InspectionBy;
	inspectionDate: string | null;
	leaseContractFile1: string | null;
	maintenanceCompanyName: string;
	maintenanceCompanyStoreName: string;
	name: string;
	orderDate: string | null;
	originVendorCompany: VendorCompany;
	originVendorUserName: string | null;
	preferredDate: string | null;
	roomNumber: string | null;
	sendEstimateDate: string | null;
	staff: Staff;
	statusId: StatusId;
	subVendorCompany: VendorCompany | null;
	subVendorUserName: string | null;
	surveyDate: string | null;
	tenantName: string | null;
	tenantNameKana: string | null;
	terminationDate: string | null;
	unreadMessageCount: number;
	updatedAt: string;
	updatedDate: string;
	user: VendorUser;
};

export type DealWithPrices = {
	companySetting: Pick<CompanySetting, "fromMarket" | "antisocialCheckId">;
	completionReportImageRequired?: boolean;
	dealEstimationContext: DealEstimationContext;
	dealEstimationHistoryContext: DealEstimationHistoryContext;
	dealOwnerBillingContext: DealBillingContext;
	dealRenteeBillingContext: DealBillingContext;
	dealSubtype: Subtype | null;
	dealType: DealType;
	// TODO: Dealに合わせて、dealVendorCompaniesから特定するようにしたい。NSKDEV-2234
	dealVendorCompanies: DealVendorCompany[];
	estimationBy: EstimationBy;
	id: number;
	inspectionBy: InspectionBy;
	inspectionDate: string | null;
	inspectionReportImageRequired?: boolean;
	originDealVendorCompany: DealVendorCompany | null;
	requestEstimationHeader: boolean;
	skPartnerDeal: PartnerDeal | null;
	statusId: StatusId;
};

/**
 * 関連する案件
 */
export type RelatedDeal = {
	createdAt: string;
	id: number;
	name: string;
	roomNumber: string | null;
	updatedAt: string;
};

export type SearchApplicationType =
	| "cancel"
	| "draftEstimation" // 施工会社が編集中の見積り
	| "draftEstimationByCompany"
	| "requestEstimation" // 施工会社が申請した見積り
	| "requestEstimationByCompany";

export type DealSearchCondition = {
	address?: string;
	applicationTypes?: SearchApplicationType[];
	authId?: number;
	companyMasterId?: number;
	companyVendorId?: number;
	completedMaxDate?: string;
	completedMinDate?: string;
	constructionMaxEndDate?: string;
	constructionMaxStartDate?: string;
	constructionMinEndDate?: string;
	constructionMinStartDate?: string;
	constructionReportedMaxDate?: string;
	constructionReportedMinDate?: string;
	dealSubtypeId?: number | null;
	dealSubtypeIds?: number[];
	dealType?: DealType;
	dealTypes?: DealType[];
	estimationRequestMaxDate?: string;
	estimationRequestMinDate?: string;
	inspectionMaxDate?: string;
	inspectionMinDate?: string;
	name?: string;
	orderMaxDate?: string;
	orderMinDate?: string;
	page?: number;
	partnerVendorId?: number;
	prioritySort?: boolean;
	registrationMaxDate?: string;
	registrationMinDate?: string;
	roomNumber?: string;
	sendEstimateMaxDate?: string;
	sendEstimateMinDate?: string;
	sortDirection?: ComponentProps<typeof Th>["sort"];
	sortKey?: DealSortKey;
	staffId?: number;
	statusId?: number[];
	surveyMaxDate?: string;
	surveyMinDate?: string;
	tenantName?: string;
	updatedMaxDate?: string;
	updatedMinDate?: string;
	userId?: number;
};

export type DealSortKey =
	| "registration_date"
	| "status_id"
	| "termination_date"
	| "inspection_date"
	| "survey_date"
	| "send_estimate_date"
	| "order_date"
	| "construction_start_date"
	| "construction_end_date"
	| "construction_reported_date"
	| "completed_date"
	| "preferred_date"
	| "updated_date";

export const isNumberCondition = (key: string): boolean =>
	[
		"companyMasterId",
		"authId",
		"companyVendorId",
		"staffId",
		"userId",
		"partnerVendorId",
		"page"
	].includes(key);

const isDetailCondition = (key: string): boolean =>
	[
		"dealTypes",
		"statusId",
		"applicationTypes",
		"name",
		"address",
		"tenantName",
		"companyMasterId",
		"companyVendorId",
		"partnerVendorId",
		"authId",
		"staffId",
		"userId",
		"constructionMinStartDate",
		"constructionMaxStartDate",
		"constructionMinEndDate",
		"constructionMaxEndDate",
		"inspectionMinDate",
		"inspectionMaxDate",
		"registrationMinDate",
		"registrationMaxDate",
		"updatedMinDate",
		"updatedMaxDate",
		"prioritySort"
	].includes(key);

export const searchDetailBadge = (
	dealSearchCondition: DealSearchCondition
): number => {
	let ret = 0;
	Object.entries(dealSearchCondition).forEach(([key, value]) => {
		if (isDetailCondition(key)) {
			if (Array.isArray(value)) {
				if (value.length > 0) {
					ret += 1;
				}
			} else if (value) {
				ret += 1;
			}
		}
	});
	return ret;
};

export const isWithVendorDeal = (deal: Deal): boolean => {
	const originDealVendor = getOriginDealVendor(deal);
	if (originDealVendor === null) {
		// 施工会社未選択は一旦施工会社も呼ぶ前提として扱う
		return true;
	}
	return originDealVendor.vendorCompany !== null;
};

export const matchDealType = (
	deal: Deal | DealIndexClient | DealIndexVendor,
	dealType: "restoration" | "repair" | "other"
): boolean => deal.dealType === dealType;

export const INSPECTION_BY_LABEL = {
	company: "管理会社",
	none: "なし",
	vendor: "施工会社"
} as const satisfies Record<InspectionBy, string>;

export const ESTIMATION_BY_LABEL = {
	company: "管理会社",
	vendor: "施工会社"
} as const satisfies Record<EstimationBy, string>;

export const DEAL_TYPE_LABEL = {
	other: "その他",
	repair: "修繕",
	restoration: "原状回復"
} as const;
// typescript4.9, next13以降からしかsatisfiesが使えない
// } as const satisfies {
//   [key in DealType]: string;
// }

// 立会報告画像が必須かどうかを判定する
export const checkInspectionImageRequired = (
	deal: Deal | DealWithPrices
): boolean =>
	deal.inspectionBy === "vendor" && !!deal.inspectionReportImageRequired;

// 案件少佐画面の見積りの状態を返す関数。それぞれの状態はすべてユニークなので順番は関係なく動作する
export const estimationHeaderStatus = (
	deal: Deal
): { color: string; description: string; label: string } => {
	// 非共有案件はheader利用しない仕様になったが、過去データが残っているため分岐させる
	// https://itandi-dev.atlassian.net/browse/NSKDEV-1622で削除
	if (isOwnStatus(deal.statusId))
		return { color: "", description: "", label: "" };
	if (deal.hasDraftEstimation)
		return {
			color: colors["expressive-yellow-dark"],
			description:
				"見積編集中のため、見積り内容は確定しておりません。見積りの修正が完了次第、見積りを申請するボタンを押してください。",
			label: "編集中"
		};
	if (deal.hasRequestEstimation)
		return {
			color: colors["expressive-blue"],
			description:
				"見積申請中のため、見積り内容は確定しておりません。見積りが反映されるまでお待ち下さい。",
			label: "申請中"
		};
	if (deal.hasRequestedEstimation)
		return {
			color: colors["expressive-aqua-dark"],
			description:
				"見積りの申請がございます。反映・承認を行い、見積内容を確定させてください。",
			label: "申請あり"
		};
	if (deal.latestEstimationRejected)
		return {
			color: "",
			description:
				"申請した見積りが棄却されております。修正を行いたい場合には、再度見積りを編集し申請をおこなってください。",
			label: ""
		};
	return { color: "", description: "", label: "" };
};

// マケプレ案件かつ反社チェックID未登録の場合は true
export const isInvalidAntisocialCheck = (
	deal: Deal | DealWithPrices
): boolean =>
	deal.companySetting.fromMarket &&
	deal.companySetting.antisocialCheckId === null;

export const formatPropertyName = (
	deal: Pick<Deal, "name" | "roomNumber">,
	maxNameLength?: number
): string => {
	const name = maxNameLength ? truncate(deal.name, maxNameLength) : deal.name;
	return `${name} ${deal.roomNumber ?? ""}`.trim();
};
